/* eslint-disable max-lines */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/jsx-handler-names */
import { useEffect, useState } from "react";
import PropType from "prop-types";
import CheckboxItemList from "../../../molecules/checkboxItemList";
import Image from "next/image";
import Typography from "../../../atoms/typography";
import { constants } from "../../../../constants/pageConstants/pdpConstants";
import TextField from "../../../atoms/textField";
import { shallowEqual, useSelector } from "react-redux";
import { updateCurrency, getIconUrl } from "../../../../utils/common";
import PriceHistogram from "../../../molecules/priceHistogram";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import { INDIAN_RUPEE, CURRENCY_ID } from "../../../../constants/common/common";
import {
  currencyConverter as currencyConverterUtil,
  reverseCurrencyConverter as reverseCurrencyConverterUtil,
  snapToStepSize,
  getFilterOptionLabel,
  getSelectedOptionsCount,
  isSameRange,
  priceToSliderValue,
  sliderValueToPrice,
} from "../../../../utils/plp/filters";
import useAnalytics from "../../../../analytics/useAnalytics";
import { usePathname } from "next/navigation";
import { cleverTapCommonWords } from "../../../../analytics/clevertapConstants";
import { plpCleverTapEvents } from "../../../../constants/pageConstants/plpConstants";

/**
 * FilterSection component for handling each filter section.
 * @param {object} param0 - The props object.
 * @returns {JSX.Element}
 */
const FilterSection = ({
  title,
  options,
  onOptionChange,
  selectedOptions,
  facetName,
  onPriceChange,
  onPriceChangeComplete,
  priceRange,
}) => {
  const [isOpen, setIsOpen] = useState(facetName === "defaultPrice");
  const [localPriceRange, setLocalPriceRange] = useState([]);
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [isInitialized, setIsInitialized] = useState(false);
  const [isProcessingInput, setIsProcessingInput] = useState(false);
  const [enterPressed, setEnterPressed] = useState(false);
  const { trackEvent } = useAnalytics();
  const pathName = usePathname();

  const { selectedCurrency } = useSelector(
    (state) => ({
      selectedCurrency: state?.currencyList?.userSelectedCurrency,
    }),
    shallowEqual,
  );

  /** */
  const currencyConverter = (value) => currencyConverterUtil(selectedCurrency, value, updateCurrency);

  /**
   * Convert from selected currency back to base currency (INR/API currency)
   * User inputs are in selected currency, but API needs base currency values
   */
  const reverseCurrencyConverter = (convertedValue) =>
    reverseCurrencyConverterUtil(selectedCurrency, convertedValue, CURRENCY_ID);

  // Initialize localPriceRange from API response (minPrice, maxPrice)
  useEffect(() => {
    if (facetName === "defaultPrice" && options?.[0]) {
      const minPriceBound = options[0].roundedMinPrice || 0;
      const maxPriceBound = options[0].roundedMaxPrice || 100000;

      // If priceRange prop is provided (from URL), adjust it to fit within new min/max bounds
      if (priceRange && Array.isArray(priceRange) && priceRange.length === 2) {
        const [selectedMin, selectedMax] = priceRange;

        // Clamp the selected range within the new min/max bounds
        const adjustedMin = Math.max(selectedMin, minPriceBound);
        const adjustedMax = Math.min(selectedMax, maxPriceBound);

        // Always set the values when priceRange is provided (from URL on refresh)
        const finalRange = [adjustedMin, adjustedMax];
        setLocalPriceRange(finalRange);
        setMinPrice(currencyConverter(adjustedMin));
        setMaxPrice(currencyConverter(adjustedMax));

        // Only notify parent if the range was adjusted (different from original)
        // This prevents unnecessary API calls on initial load
        if ((adjustedMin !== selectedMin || adjustedMax !== selectedMax) && onPriceChange && isInitialized) {
          onPriceChange(finalRange);
        }
        setIsInitialized(true);
      } else if (!isInitialized || !priceRange) {
        // Set defaults if not initialized or no priceRange provided
        setLocalPriceRange([minPriceBound, maxPriceBound]);
        setMinPrice(currencyConverter(minPriceBound));
        setMaxPrice(currencyConverter(maxPriceBound));
        setIsInitialized(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facetName, options, priceRange, selectedCurrency?.currency?.currencyCode]);

  /**
   * Snap value to appropriate step size based on outlier threshold
   * @param {number} value - The value to snap
   * @returns {number} Snapped value
   */
  const snapToStepSizeLocal = (value) => snapToStepSize(value, options?.[0]);

  /**
   * Handle price range changes from slider (slider values are 0-100, need to convert to actual prices)
   */
  const handlePriceChange = (newRange) => {
    const [sliderMin, sliderMax] = newRange;
    const priceOptions = options?.[0];

    // Convert slider positions (0-100) to actual price values
    const min = sliderValueToPrice(sliderMin, priceOptions);
    const max = sliderValueToPrice(sliderMax, priceOptions);

    const roundedMaxPrice = options?.[0]?.roundedMaxPrice;
    const roundedMinPrice = options?.[0]?.roundedMinPrice;

    // Clamp values to valid bounds first
    const clampedMin = Math.max(roundedMinPrice, Math.min(min, roundedMaxPrice));
    const clampedMax = Math.max(roundedMinPrice, Math.min(max, roundedMaxPrice));

    // Snap values to appropriate step sizes
    const snappedMin = snapToStepSizeLocal(clampedMin);
    const snappedMax = snapToStepSizeLocal(clampedMax);

    // Ensure min doesn't exceed max and both are within bounds (in base currency)
    const finalMin = Math.min(snappedMin, snappedMax, roundedMaxPrice);
    const finalMax = Math.max(snappedMin, snappedMax);
    const snappedRange = [finalMin, finalMax];

    // Store in base currency (INR) for API
    setLocalPriceRange(snappedRange);
    // Display converted values in UI (selected currency)
    setMinPrice(currencyConverter(finalMin));
    setMaxPrice(currencyConverter(finalMax));
    // Call parent's onPriceChange immediately - debouncing is handled in parent
    onPriceChange && onPriceChange(snappedRange);
  };

  /**
   * Toggle filters category on/off.
   */
  const onToggle = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    if (isOpen) {
      trackEvent(
        plpCleverTapEvents?.FILTER_VIEWED,
        { page_name: pathName, page_type: cleverTapCommonWords?.PLP, filter_attribute: title },
        ["clevertap"],
      );
    }
  }, [isOpen]);

  /**
   * Get label for the option
   * @param {Object} option - The option object
   * @returns {string} Formatted label
   */
  const getLabel = (option) => getFilterOptionLabel(option);

  /**
   * Calculates the number of selected options with a count greater than zero.
   *
   * @returns {number} The count of options that are selected and have a count greater than zero.
   */
  const handleCatergoryCount = () => getSelectedOptionsCount(options, selectedOptions);

  /** */
  const returnInputValue = (isMin) => {
    if (isMin) {
      return minPrice || 0;
    } else {
      return maxPrice || 0;
    }
  };

  /** */
  const onHandleFieldChange = (e, isMin) => {
    const { value } = e.target;

    if (value === "") {
      // Allow empty string
      if (isMin) {
        setMinPrice("");
      } else {
        setMaxPrice("");
      }
      return;
    }

    // Remove non-numeric characters and leading zeros
    let numericValue = value.replace(/[^0-9]/g, "");

    // Remove leading zeros but keep single zero
    if (numericValue.length > 1) {
      numericValue = numericValue.replace(/^0+/, "");
    }

    // If all zeros were removed, keep it as empty string
    if (numericValue === "") {
      numericValue = "";
    }

    if (isMin) {
      setMinPrice(numericValue);
    } else {
      setMaxPrice(numericValue);
    }
  };

  /** */
  const onClickGo = () => {
    if (isProcessingInput) return;
    setIsProcessingInput(true);

    // Get the min and max bounds from the API (in base currency - INR)
    const minBound = options?.[0]?.roundedMinPrice || 0;
    const maxBound = options?.[0]?.roundedMaxPrice || 100000;

    // User input (minPrice/maxPrice) is in selected currency, convert to base currency (INR)
    const minValueInBase = minPrice === "" ? minBound : reverseCurrencyConverter(minPrice);
    const maxValueInBase = maxPrice === "" ? maxBound : reverseCurrencyConverter(maxPrice);

    // Clamp to valid bounds (in base currency)
    const clampedMin = Math.max(minBound, Math.min(minValueInBase, maxBound));
    const clampedMax = Math.max(minBound, Math.min(maxValueInBase, maxBound));

    // Ensure min is not greater than max (in base currency)
    const finalMinBase = Math.min(clampedMin, clampedMax);
    const finalMaxBase = Math.max(clampedMin, clampedMax);

    // Store in base currency (INR) for API - this is what gets sent to API
    setLocalPriceRange([finalMinBase, finalMaxBase]);

    // Display converted values in UI (selected currency)
    setMinPrice(currencyConverter(finalMinBase));
    setMaxPrice(currencyConverter(finalMaxBase));

    // Trigger visual update (pass base currency values)
    onPriceChange && onPriceChange([finalMinBase, finalMaxBase]);

    // Only trigger API call if the range really changed (using base currency values)
    if (!isSameRange([finalMinBase, finalMaxBase], priceRange)) {
      onPriceChangeComplete && onPriceChangeComplete([finalMinBase, finalMaxBase]);
    }

    // Reset flag after a short delay
    setTimeout(() => {
      setIsProcessingInput(false);
    }, 100);
  };

  /** */
  const onBlur = () => {
    // Skip if Enter was just pressed to avoid duplicate calls
    if (enterPressed) return;
    onClickGo();
  };

  /** */
  const onFocus = (e) => {
    // Select all text when user focuses on the input
    e.target.select();
  };

  /** */
  const onKeyDown = (e) => {
    // Trigger validation when user presses Enter
    if (e.key === "Enter") {
      e.preventDefault(); // Prevent default behavior
      setEnterPressed(true); // Mark that Enter was pressed
      onClickGo();
      // Reset the flag after processing
      setTimeout(() => {
        setEnterPressed(false);
      }, 100);
    }
  };

  /** */
  const renderPriceRangeBox = (title, isMin = false) => {
    return (
      <div className="flex flex-col gap-2 md:w-[116px]">
        <Typography classes="text-12 font-400 leading-16 text-black-100 w-full text-center" Tag="p" variant="p">
          {title}
        </Typography>
        <div className="flex w-full items-center justify-center p-4">
          <TextField
            type="text"
            inputValue={isMin ? returnInputValue(true) : returnInputValue()}
            className={`w-full border-2 border-thinLightGrey p-4 text-12 font-400 ${selectedCurrency?.currency?.id === "INR" ? "pl-24" : "pl-42"}`}
            onChange={(e) => {
              onHandleFieldChange(e, isMin);
            }}
            onBlur={onBlur}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
            startAdornment={
              <Typography variant="p" Tag="p" classes="text-12 font-400">
                {selectedCurrency?.currency?.currencyCode}
              </Typography>
            }
          />
        </div>
      </div>
    );
  };

  // Get histogram data and marks
  const priceBuckets = options?.[0]?.priceBuckets || [];

  return (
    <div className="w-full max-w-full overflow-hidden border-t border-gray-100 px-20 py-16">
      <button
        className={`flex w-full items-center justify-between text-left focus:outline-none ${isOpen ? "mb-20" : ""}`}
        onClick={onToggle}
      >
        <span className={`text-14 font-500 ${handleCatergoryCount() > 0 ? "font-600" : "font-500"}`}>{title}</span>
        {isOpen ? (
          <Image src={getIconUrl("/icons/minus.svg")} alt="close" className="p-1" width={22} height={22} />
        ) : (
          <Image src={getIconUrl("/icons/add.svg")} alt="open" className="p-1" width={22} height={22} />
        )}
      </button>
      {isOpen && (
        <>
          {facetName === "defaultPrice" ? (
            // Price filter with range slider
            <div className="space-y-16">
              {/* Histogram */}
              {priceBuckets.length > 0 && (
                <div className="mb-4 w-full">
                  <PriceHistogram
                    priceBuckets={priceBuckets}
                    effectiveMaxPrice={options?.[0]?.effectiveMaxPrice}
                    onBarClick={(convertedRange) => {
                      // convertedRange is in selected currency from PriceHistogram
                      // Convert back to base currency (INR) for API
                      const baseMin = reverseCurrencyConverter(convertedRange[0]);
                      const baseMax = reverseCurrencyConverter(convertedRange[1]);
                      const priceOptions = options?.[0];
                      const roundedMaxPrice = priceOptions?.roundedMaxPrice;
                      const roundedMinPrice = priceOptions?.roundedMinPrice;

                      // Clamp values to valid bounds
                      const clampedMin = Math.max(roundedMinPrice, Math.min(baseMin, roundedMaxPrice));
                      const clampedMax = Math.max(roundedMinPrice, Math.min(baseMax, roundedMaxPrice));

                      // Snap values to appropriate step sizes
                      const snappedMin = snapToStepSizeLocal(clampedMin);
                      const snappedMax = snapToStepSizeLocal(clampedMax);

                      // Ensure min doesn't exceed max
                      const finalMin = Math.min(snappedMin, snappedMax, roundedMaxPrice);
                      const finalMax = Math.max(snappedMin, snappedMax);
                      const finalRange = [finalMin, finalMax];

                      // Store in base currency (INR) for API
                      setLocalPriceRange(finalRange);
                      // Display converted values in UI (selected currency)
                      setMinPrice(currencyConverter(finalMin));
                      setMaxPrice(currencyConverter(finalMax));
                      // Trigger API call
                      onPriceChangeComplete && onPriceChangeComplete(finalRange);
                    }}
                    currencyConverter={currencyConverter}
                  />
                </div>
              )}

              {/* Slider Labels */}
              <div className="flex w-full items-center justify-between text-10 text-gray-500">
                <span>{`${selectedCurrency?.currency?.currencyCode || INDIAN_RUPEE} ${currencyConverter(options?.[0]?.roundedMinPrice)}`}</span>
                <span>
                  {`${selectedCurrency?.currency?.currencyCode || INDIAN_RUPEE} ${currencyConverter(
                    options?.[0]?.isSkewedDistribution
                      ? options?.[0]?.effectiveMaxPrice
                      : options?.[0]?.roundedMaxPrice,
                  )}`}
                  {options?.[0]?.isSkewedDistribution && <span className="font-semibold text-gray-600">+</span>}
                </span>
              </div>

              {/* Range Slider */}
              <div className="w-full max-w-full px-8">
                <Slider
                  range={true}
                  min={0}
                  max={100}
                  step={0.1}
                  value={
                    localPriceRange.length > 0
                      ? [
                          priceToSliderValue(localPriceRange[0], options?.[0]),
                          priceToSliderValue(localPriceRange[1], options?.[0]),
                        ]
                      : [0, 100]
                  }
                  onChange={handlePriceChange}
                  onChangeComplete={(sliderRange) => {
                    // Convert slider positions to actual prices
                    const [sliderMin, sliderMax] = sliderRange;
                    const priceOptions = options?.[0];
                    const min = sliderValueToPrice(sliderMin, priceOptions);
                    const max = sliderValueToPrice(sliderMax, priceOptions);

                    const roundedMaxPrice = options?.[0]?.roundedMaxPrice;
                    const roundedMinPrice = options?.[0]?.roundedMinPrice;

                    // Clamp values to valid bounds
                    const clampedMin = Math.max(roundedMinPrice, Math.min(min, roundedMaxPrice));
                    const clampedMax = Math.max(roundedMinPrice, Math.min(max, roundedMaxPrice));

                    // Snap values to appropriate step sizes
                    const snappedMin = snapToStepSizeLocal(clampedMin);
                    const snappedMax = snapToStepSizeLocal(clampedMax);

                    // Ensure min doesn't exceed max
                    const finalMin = Math.min(snappedMin, snappedMax, roundedMaxPrice);
                    const finalMax = Math.max(snappedMin, snappedMax);
                    const finalRange = [finalMin, finalMax];

                    // Update local state
                    setLocalPriceRange(finalRange);
                    setMinPrice(currencyConverter(finalMin));
                    setMaxPrice(currencyConverter(finalMax));

                    // Trigger API call
                    onPriceChangeComplete && onPriceChangeComplete(finalRange);
                  }}
                  marks={
                    options?.[0]?.isSkewedDistribution && options?.[0]?.effectiveMaxPrice
                      ? {
                          70: {
                            style: {
                              borderLeft: "1px solid #9CA3AF",
                              height: "12px",
                              width: "1px",
                              marginTop: "-4px",
                            },
                            label: "",
                          },
                        }
                      : {}
                  }
                  styles={{
                    track: {
                      backgroundColor: "#737530",
                      height: "4px",
                    },
                    handle: {
                      backgroundColor: "#FFFFFF",
                      borderColor: "#737530",
                      borderWidth: "1px",
                      width: "16px",
                      height: "16px",
                      marginTop: "-6px",
                      boxShadow: "none",
                    },
                    rail: {
                      backgroundColor: "#E5E7EB",
                      height: "4px",
                    },
                  }}
                />
              </div>

              {/* Input Fields */}
              <div className="flex w-full items-center">
                <div className="flex w-full items-center justify-between gap-8">
                  {renderPriceRangeBox(constants?.MINIMUM, true)}
                  {renderPriceRangeBox(constants?.MAXIMUM)}
                </div>
              </div>
            </div>
          ) : (
            // Regular checkbox filters
            options?.map(
              (option, index) =>
                option.count > 0 && (
                  <div key={option.displayName} className="flex items-center py-10 text-14">
                    <CheckboxItemList
                      id={`${facetName}_${option.displayName}_${index}`}
                      label={getLabel(option)}
                      value={option.displayName}
                      onChange={onOptionChange}
                      data-parentfilter={facetName}
                      isChecked={!!selectedOptions?.[option.displayName]}
                      labelColor="text-grey-500 text-14"
                      labelPlacement="largeGap"
                    />
                  </div>
                ),
            )
          )}
        </>
      )}
    </div>
  );
};

FilterSection.propTypes = {
  title: PropType.string.isRequired,
  options: PropType.array.isRequired,
  onOptionChange: PropType.func.isRequired,
  selectedOptions: PropType.object.isRequired,
  facetName: PropType.string.isRequired,
  onPriceChange: PropType.func,
  onPriceChangeComplete: PropType.func,
  priceRange: PropType.array,
};

export default FilterSection;
